import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Banknote, FileSignature, Sparkles } from "lucide-react";
import { authApi } from "../lib/api";

/**
 * Shows a one-time-per-release "What's New" modal after login.
 * Backend tracks acknowledgment per user via /auth/release + /auth/mark-release-seen.
 */
export default function WhatsNewDialog() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await authApi.getRelease();
        if (!cancelled && res?.data && res.data.user_seen === false) {
          setOpen(true);
        }
      } catch {
        // Silent: if endpoint not reachable, just skip.
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAck = async () => {
    try {
      await authApi.markReleaseSeen();
    } catch {
      // Even if the network call fails, close so we don't loop.
    } finally {
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleAck()}>
      <DialogContent
        className="sm:max-w-[560px]"
        data-testid="whats-new-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs font-semibold uppercase tracking-wider">
            <Sparkles size={14} />
            What&apos;s New
          </div>
          <DialogTitle className="text-2xl">
            Two new modules just landed
          </DialogTitle>
          <DialogDescription>
            A quick heads-up on what&apos;s been added to the portal since you last
            stopped by.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div className="rounded-lg border border-border bg-card p-4 flex gap-3">
            <div
              className="rounded-md shrink-0 w-10 h-10 flex items-center justify-center"
              style={{ backgroundColor: "rgba(16,185,129,0.12)" }}
            >
              <Banknote size={20} className="text-emerald-600" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-sm" data-testid="whats-new-cfp">
                Client Financial Platform
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Single-sign-on into our client books, financials & reports app
                straight from the sidebar. No second password — your portal
                login carries over automatically.
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4 flex gap-3">
            <div
              className="rounded-md shrink-0 w-10 h-10 flex items-center justify-center"
              style={{ backgroundColor: "rgba(59,130,246,0.12)" }}
            >
              <FileSignature size={20} className="text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-sm" data-testid="whats-new-w9">
                Request W-9 Module
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Send vendors a secure link to complete their W-9 right inside
                their browser. You&apos;ll get an official IRS-format PDF emailed
                back, with SSN / EIN encrypted at rest. View or download any
                completed W-9 from the staff dashboard.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button
            onClick={handleAck}
            data-testid="whats-new-got-it"
            className="w-full sm:w-auto"
          >
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
